import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { announcementsPublicApi, eventsPublicApi, scheduledClosingsPublicApi } from '../../api/adminApi';
import {
  announcementId,
  announcementBody,
  announcementDateInputValue,
  formatAnnouncementDateShort,
  announcementDescriptionPreview,
} from '../Admin/Announcements/announcementHelpers';
import {
  eventId,
  eventDescription,
  eventDateInputValue,
  formatEventCardMonthDay,
  eventDescriptionPreview,
} from '../Admin/Events/eventHelpers';
import {
  scheduledClosingId,
  scheduledClosingDateInputValue,
  formatScheduledClosingCardMonthDay,
} from '../Admin/ScheduledClosings/scheduledClosingHelpers';
import './NewsCalendarPage.css';

function NewsCalendarPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [announcementsError, setAnnouncementsError] = useState('');

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState('');

  const [closings, setClosings] = useState([]);
  const [closingsLoading, setClosingsLoading] = useState(true);
  const [closingsError, setClosingsError] = useState('');

  /** Expanded announcement descriptions on list cards (read more). */
  const [announcementExpanded, setAnnouncementExpanded] = useState({});

  /** Expanded event descriptions on list cards (read more). */
  const [eventExpanded, setEventExpanded] = useState({});

  useEffect(() => {
    let cancelled = false;
    setAnnouncementsLoading(true);
    setAnnouncementsError('');
    announcementsPublicApi
      .list(1, 50)
      .then((res) => {
        if (!cancelled) setAnnouncements(res.list || []);
      })
      .catch(() => {
        if (!cancelled) {
          setAnnouncements([]);
          setAnnouncementsError('Unable to load announcements right now. Please try again later.');
        }
      })
      .finally(() => {
        if (!cancelled) setAnnouncementsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setEventsLoading(true);
    setEventsError('');
    eventsPublicApi
      .list(1, 50)
      .then((res) => {
        if (!cancelled) setEvents(res.list || []);
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([]);
          setEventsError('Unable to load events right now. Please try again later.');
        }
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setClosingsLoading(true);
    setClosingsError('');
    scheduledClosingsPublicApi
      .list(1, 100)
      .then((res) => {
        if (!cancelled) setClosings(res.list || []);
      })
      .catch(() => {
        if (!cancelled) {
          setClosings([]);
          setClosingsError('Unable to load scheduled closings right now. Please try again later.');
        }
      })
      .finally(() => {
        if (!cancelled) setClosingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedAnnouncements = useMemo(() => {
    return [...announcements].sort((a, b) => {
      const da = announcementDateInputValue(a) || '';
      const db = announcementDateInputValue(b) || '';
      return db.localeCompare(da);
    });
  }, [announcements]);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const da = eventDateInputValue(a) || '';
      const db = eventDateInputValue(b) || '';
      return da.localeCompare(db);
    });
  }, [events]);

  const sortedClosings = useMemo(() => {
    return [...closings].sort((a, b) => {
      const da = scheduledClosingDateInputValue(a) || '';
      const db = scheduledClosingDateInputValue(b) || '';
      return da.localeCompare(db);
    });
  }, [closings]);

  return (
    <main className="news-calendar-page">
      {/* Hero Section */}
      <section className="news-calendar-hero" aria-label="News and school calendar">
        <div className="news-calendar-hero-inner">
          <h1 className="news-calendar-hero-title">News & School Calendar</h1>
          <p className="news-calendar-hero-desc">
            Stay updated with school events, holidays, and important announcements.
          </p>
        </div>
      </section>

      <div className="news-calendar-content">
        {/* Announcements */}
        <section className="news-calendar-section" aria-label="Announcements">
          <h2 className="news-calendar-section-title">Announcements</h2>
          {announcementsError && (
            <p className="news-calendar-announcements-error" role="alert">
              {announcementsError}
            </p>
          )}
          {announcementsLoading ? (
            <p className="news-calendar-announcements-loading">Loading announcements…</p>
          ) : sortedAnnouncements.length === 0 && !announcementsError ? (
            <p className="news-calendar-announcements-empty">Check back soon for updates.</p>
          ) : (
            <div className="news-calendar-announcements">
              {sortedAnnouncements.map((item) => {
                const aid = announcementId(item);
                const desc = (announcementBody(item) || '').trim();
                const { preview, isTruncated } = announcementDescriptionPreview(desc);
                const expanded = Boolean(announcementExpanded[aid]);
                const dateStr = formatAnnouncementDateShort(announcementDateInputValue(item));
                const dateIso = announcementDateInputValue(item);
                if (!aid) return null;
                const showFullDesc = !isTruncated || expanded;
                const descToShow = showFullDesc ? desc : preview;

                return (
                  <article key={aid} className="news-calendar-announcement-card">
                    <time className="news-calendar-announcement-date" dateTime={dateIso || undefined}>
                      {dateStr}
                    </time>
                    <h3 className="news-calendar-announcement-title">
                      <Link to={`/announcements/${aid}`} className="news-calendar-announcement-title-link">
                        {item.title || 'Announcement'}
                      </Link>
                    </h3>
                    {desc ? (
                      <div className="news-calendar-announcement-desc-wrap">
                        <p className="news-calendar-announcement-desc">
                          {descToShow}
                          {!showFullDesc ? '…' : ''}
                        </p>
                        {isTruncated ? (
                          <button
                            type="button"
                            className="news-calendar-announcement-read-toggle"
                            onClick={() =>
                              setAnnouncementExpanded((prev) => ({
                                ...prev,
                                [aid]: !prev[aid],
                              }))
                            }
                            aria-expanded={expanded}
                          >
                            {expanded ? 'Read less' : 'Read more'}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    <Link to={`/announcements/${aid}`} className="news-calendar-announcement-full-page">
                      View full announcement
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Upcoming Events & Activities - Full width background */}
      <section className="news-calendar-section news-calendar-section--events" aria-label="Upcoming events and activities">
        <div className="news-calendar-events-inner">
          <h2 className="news-calendar-section-title">Upcoming Events & Activities</h2>
          {eventsError && (
            <p className="news-calendar-events-error" role="alert">
              {eventsError}
            </p>
          )}
          {eventsLoading ? (
            <p className="news-calendar-events-loading">Loading events…</p>
          ) : sortedEvents.length === 0 && !eventsError ? (
            <p className="news-calendar-events-empty">No upcoming events scheduled. Check back soon.</p>
          ) : (
            <div className="news-calendar-events-grid">
              {sortedEvents.map((ev) => {
                const eid = eventId(ev);
                const desc = (eventDescription(ev) || '').trim();
                const { preview, isTruncated } = eventDescriptionPreview(desc);
                const expanded = Boolean(eventExpanded[eid]);
                const { month, day } = formatEventCardMonthDay(ev);
                const dateIso = eventDateInputValue(ev);
                if (!eid) return null;
                const showFullDesc = !isTruncated || expanded;
                const descToShow = showFullDesc ? desc : preview;

                return (
                  <article key={eid} className="news-calendar-event-card">
                    <div className="news-calendar-event-date">
                      <time dateTime={dateIso || undefined}>
                        <span className="news-calendar-event-month">{month}</span>
                        <span className="news-calendar-event-day">{day}</span>
                      </time>
                    </div>
                    <div className="news-calendar-event-body">
                      <h3 className="news-calendar-event-title">
                        <Link to={`/events/${eid}`} className="news-calendar-event-title-link">
                          {ev.title || 'Event'}
                        </Link>
                      </h3>
                      {desc ? (
                        <div className="news-calendar-event-desc-wrap">
                          <p className="news-calendar-event-desc">
                            {descToShow}
                            {!showFullDesc ? '…' : ''}
                          </p>
                          {isTruncated ? (
                            <button
                              type="button"
                              className="news-calendar-event-read-toggle"
                              onClick={() =>
                                setEventExpanded((prev) => ({
                                  ...prev,
                                  [eid]: !prev[eid],
                                }))
                              }
                              aria-expanded={expanded}
                            >
                              {expanded ? 'Read less' : 'Read more'}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      <Link to={`/events/${eid}`} className="news-calendar-event-details-link">
                        Details
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <div className="news-calendar-content">
        {/* Scheduled Closing Dates */}
        <section className="news-calendar-section" aria-label="Scheduled closing dates">
          <h2 className="news-calendar-section-title">Scheduled Closing Dates</h2>
          {closingsError && (
            <p className="news-calendar-closings-error" role="alert">
              {closingsError}
            </p>
          )}
          {closingsLoading ? (
            <p className="news-calendar-closings-loading">Loading scheduled closings…</p>
          ) : sortedClosings.length === 0 && !closingsError ? (
            <p className="news-calendar-closings-empty">No scheduled closings listed. Check back later.</p>
          ) : (
            <div className="news-calendar-closings-grid">
              {sortedClosings.map((row) => {
                const cid = scheduledClosingId(row);
                const { month, day } = formatScheduledClosingCardMonthDay(row);
                const dateIso = scheduledClosingDateInputValue(row);
                if (!cid) return null;
                return (
                  <article key={cid} className="news-calendar-closing-card">
                    <div className="news-calendar-closing-date">
                      <time dateTime={dateIso || undefined}>
                        <span className="news-calendar-closing-month">{month}</span>
                        <span className="news-calendar-closing-day">{day}</span>
                      </time>
                    </div>
                    <div className="news-calendar-closing-body">
                      <p className="news-calendar-closing-title">{row.title || 'Closing'}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
      {/* CTA */}
      <section className="cta" id="schedule-tour">
        <div className="cta-inner">
          <h2 className="cta-title">Join Our Preschool & Afterschool Family</h2>
          <p className="cta-desc">Give your child the best beginning they deserve. Schedule a tour today and see why families love The David Foster Preschool & Afterschool Academy.</p>
          <div className="cta-actions">
            <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary cta-primary">
              <span className="btn-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="3"
                    y="4"
                    width="14"
                    height="13"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                </svg>
              </span>
              Schedule Tour
            </Link>
            <Link to="/contact" className="btn btn-outline cta-secondary">
              Inquiry Now
            </Link>
          </div>

          <div className="cta-meta" aria-label="Contact and hours">
            <a className="cta-meta-item" href="tel:718-462-0776">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 3.5L9.2 7.2C9.4 7.7 9.3 8.3 8.9 8.7L7.6 10C9 12.7 11.3 15 14 16.4L15.3 15.1C15.7 14.7 16.3 14.6 16.8 14.8L20.5 16.5C21.1 16.8 21.3 17.4 21.1 18C20.6 19.5 19.2 20.5 17.6 20.5C10.1 20.5 3.5 13.9 3.5 6.4C3.5 4.8 4.5 3.4 6 2.9C6.6 2.7 7.2 2.9 7.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">718-462-0776</span>
            </a>

            <div className="cta-meta-item">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V12L14.5 13.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">Monday - Friday: 8:00 AM - 7:00 PM</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default NewsCalendarPage;
